import { CheckCircle2, ShieldCheck, XCircle } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface Approval {
  id: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  summary: string;
  requested_by: number | null;
  decided_by: number | null;
  decision_note: string | null;
}

/**
 * The reusable approval strip: request, see state, decide.
 *
 * One component for every gated action (campaign sends today, finance and AI
 * actions tomorrow) so approval always looks and behaves the same. The
 * server owns every rule, who may decide, no self-approval, content-hash
 * binding, this strip only renders state and forwards intents, so a
 * modified client changes nothing.
 */
export const ApprovalGate = ({
  actionType,
  subjectTable,
  subjectId,
  summary,
  onChanged,
}: {
  actionType: string;
  subjectTable: string;
  subjectId: string;
  summary: string;
  onChanged?: () => void;
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const workspaceId = getActiveWorkspaceId();
  const { identity } = useGetIdentity();

  const { data: required } = useQuery<boolean>({
    queryKey: ["approval-required", workspaceId, actionType],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("workspaces")
        .select("approval_required_for")
        .eq("id", workspaceId!)
        .single();
      if (error) throw new Error(error.message);
      return (
        (data.approval_required_for as string[] | null)?.includes(actionType) ??
        false
      );
    },
  });

  const { data: approval } = useQuery<Approval | null>({
    queryKey: ["approval", subjectTable, subjectId, actionType],
    enabled: Boolean(workspaceId) && required === true,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("approvals")
        .select("id,status,summary,requested_by,decided_by,decision_note")
        .eq("subject_table", subjectTable)
        .eq("subject_id", subjectId)
        .eq("action_type", actionType)
        .neq("status", "cancelled")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as Approval | null;
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["approval"] });
    onChanged?.();
  };

  if (!required) return null;

  const request = async () => {
    const { error } = await getSupabaseClient().rpc("request_approval", {
      p_action_type: actionType,
      p_subject_table: subjectTable,
      p_subject_id: subjectId,
      p_summary: summary,
      p_payload: {},
      p_workspace_id: workspaceId,
    });
    if (error) notify(error.message, { type: "error" });
    else notify("Approval requested, approvers have been notified");
    refresh();
  };

  const decide = async (approve: boolean) => {
    const { error } = await getSupabaseClient().rpc("decide_approval", {
      p_approval_id: approval!.id,
      p_approve: approve,
    });
    // "You cannot approve your own request" surfaces here verbatim.
    if (error) notify(error.message, { type: "error" });
    else notify(approve ? "Approved" : "Rejected");
    refresh();
  };

  const isRequester =
    approval?.requested_by != null && approval.requested_by === identity?.id;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2">
      <ShieldCheck className="size-4 shrink-0 text-muted-foreground" />
      {approval?.status === "approved" ? (
        <span className="flex items-center gap-1 text-[12.5px] text-emerald-700">
          <CheckCircle2 className="size-3.5" />
          Approved, any edit re-requires approval
        </span>
      ) : approval?.status === "rejected" ? (
        <span className="flex items-center gap-1 text-[12.5px] text-red-700">
          <XCircle className="size-3.5" />
          Rejected{approval.decision_note ? `, ${approval.decision_note}` : ""}
        </span>
      ) : approval?.status === "pending" ? (
        <span className="text-[12.5px] text-muted-foreground">
          Waiting for approval
        </span>
      ) : (
        <span className="text-[12.5px] text-muted-foreground">
          This action needs approval before it can run
        </span>
      )}

      <span className="ml-auto flex gap-2">
        {approval?.status === "pending" && !isRequester ? (
          <>
            <Button size="sm" className="h-7" onClick={() => decide(true)}>
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => decide(false)}
            >
              Reject
            </Button>
          </>
        ) : null}
        {!approval || approval.status === "rejected" ? (
          <Button size="sm" variant="outline" className="h-7" onClick={request}>
            Request approval
          </Button>
        ) : null}
      </span>
    </div>
  );
};
